/**
 * Customer CAI v1 is deliberately a small, prop/config-driven read surface.
 * It does not fetch, write, persist, or invoke an AI provider. The account
 * flag is evaluated before any customer content is rendered.
 */

import { isCaiGenerationEnabled } from "./caiGenerationEntitlement";
export const CAI_CUSTOMER_ELIGIBILITY_FLAG = "current_asset_intelligence_customer_enabled";

const MAX_TEXT = 280;
const MAX_ITEMS = 5;
const MAX_REVIEWED_OUTPUT_PREVIEWS = 3;
const MAX_REVIEWED_OUTPUT_PREVIEW_SECTIONS = 4;
const MAX_REVIEWED_OUTPUT_ARTIFACTS = 2;
const MAX_REVIEWED_OUTPUT_ARTIFACT_SECTIONS = 6;
const MAX_REVIEWED_OUTPUT_ARTIFACT_BULLETS = 5;
const UNSAFE_TEXT = /raw\s*(prompt|provider\s*response|payload|html|model[_ ]?output)|provider\s*response|\bprompt\b|supabase|sql|table\s*name|\btable[_ -]?[a-z0-9_]+\b|internal_cai_snapshots_v1|cai_asset_registry|opportunity_action_briefs|entity_builder_recommendations|prompt_results|env(?:ironment)?\s*value|token|stack\s*trace|service[- ]?role|bearer|request\s*(id|metadata)|\b(?:account|run|operator|request)[_ ]?id\b|\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b|header|cookie|provider\s*metadata|route\s*metadata|internal\s*diagnostic|secret/i;

export type CustomerCaiReadOnlyItem = {
  title: string;
  detail: string;
  priority?: string;
};

export type CustomerCaiOutputRecommendation = {
  title: string;
  outputType: string;
  detail: string;
  sourceInsight: string;
  audience?: string;
  evidencePattern?: string;
  expectedResult?: string;
  status?: string;
  priority?: string;
  sourceRecommendationId?: string;
};

export type CustomerCaiOutputPrescription = {
  outputType: string;
  targetAsset: string;
  objective: string;
  evidenceReason: string;
  primaryNextAction: string;
  audience: string;
  evidencePattern: string;
  expectedResult: string;
  platformFit: string;
  sourceRecommendationTitle: string;
  sourceRecommendationId?: string;
  actionBriefStatus?: string;
};

export type CustomerCaiReviewedOutputPreviewSection = {
  heading: string;
  body: string;
};

export type CustomerCaiReviewedOutputPreview = {
  title: string;
  outputType: string;
  linkedRecommendationTitle: string;
  summary: string;
  sections: CustomerCaiReviewedOutputPreviewSection[];
  status?: string;
  priority?: string;
};

export type CustomerCaiReviewedOutputArtifactSection = {
  heading: string;
  body: string;
  bullets: string[];
};

export type CustomerCaiReviewedOutputArtifact = {
  title: string;
  outputType: string;
  linkedRecommendationTitle: string;
  status?: string;
  priority?: string;
  summary: string;
  sections: CustomerCaiReviewedOutputArtifactSection[];
  reviewerNote?: string;
};

export type CustomerCaiReadOnlyModel = {
  summary: string;
  readiness: string;
  assetsReviewed: number | null;
  /** Preferred customer-facing metric; assetsReviewed remains for legacy inputs. */
  reviewedInputLabel: string;
  reviewedInputCount: number | null;
  priorityOpportunities: CustomerCaiReadOnlyItem[];
  assetGaps: CustomerCaiReadOnlyItem[];
  recommendedNextActions: string[];
  outputPrescription: CustomerCaiOutputPrescription;
  outputRecommendations: CustomerCaiOutputRecommendation[];
  reviewedOutputPreviews: CustomerCaiReviewedOutputPreview[];
  reviewedOutputArtifacts: CustomerCaiReviewedOutputArtifact[];
};

type AccountLike = {
  status?: string | null;
  local_config?: Record<string, unknown> | null;
  [CAI_CUSTOMER_ELIGIBILITY_FLAG]?: boolean;
};

function safeText(value: unknown, fallback = ""): string {
  const text = Array.from(String(value ?? ""))
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  return !text || UNSAFE_TEXT.test(text) ? fallback : text.slice(0, MAX_TEXT);
}

function safeCount(value: unknown): number | null {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? Math.min(Math.floor(count), 10000) : null;
}

function safeItems(value: unknown): CustomerCaiReadOnlyItem[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_ITEMS).map((item) => {
    const source = item && typeof item === "object" ? item as Record<string, unknown> : {};
    return {
      title: safeText(source.title || source.label || source.name, "Review area"),
      detail: safeText(source.detail || source.summary || source.description || source.rationale, "Additional detail is not available in this read model."),
      priority: safeText(source.priority, "") || undefined,
    };
  });
}

function safeOutputRecommendations(value: unknown): CustomerCaiOutputRecommendation[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_ITEMS).map((item) => {
    const source = item && typeof item === "object" ? item as Record<string, unknown> : {};
    const sourceRecommendationId = safeText(source.id || source.recommendationId || source.recommendation_id, "");
    return {
      title: safeText(source.title || source.label || source.name, "Recommended deliverable"),
      outputType: safeText(source.outputType || source.output_type || source.type, "Read-only brief"),
      detail: safeText(source.detail || source.summary || source.description || source.rationale, "A customer-safe deliverable could be scoped from this insight."),
      sourceInsight: safeText(source.sourceInsight || source.source_insight || source.insight || source.evidence, "Based on the current account review."),
      ...(safeText(source.audience || source.targetAudience || source.target_audience, "") ? { audience: safeText(source.audience || source.targetAudience || source.target_audience) } : {}),
      ...(safeText(source.evidencePattern || source.evidence_pattern || source.evidenceSignal || source.evidence_signal, "") ? { evidencePattern: safeText(source.evidencePattern || source.evidence_pattern || source.evidenceSignal || source.evidence_signal) } : {}),
      ...(safeText(source.expectedResult || source.expected_result || source.deliverableOutcome || source.deliverable_outcome, "") ? { expectedResult: safeText(source.expectedResult || source.expected_result || source.deliverableOutcome || source.deliverable_outcome) } : {}),
      status: safeText(source.status, "") || undefined,
      priority: safeText(source.priority, "") || undefined,
      ...(sourceRecommendationId ? { sourceRecommendationId } : {}),
    };
  });
}

function safeOutputPrescription(
  value: unknown,
  outputRecommendation: CustomerCaiOutputRecommendation | undefined,
  priorityOpportunity: CustomerCaiReadOnlyItem | undefined,
  recommendedNextAction: string | undefined,
): CustomerCaiOutputPrescription {
  const source = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const fallbackTitle = outputRecommendation?.title || priorityOpportunity?.title || "";
  const fallbackReason = outputRecommendation?.sourceInsight || priorityOpportunity?.detail || "Review evidence before creating the next asset.";
  const fallbackAction = recommendedNextAction || outputRecommendation?.detail || "Review evidence before creating the next asset.";
  const fallbackAudience = outputRecommendation?.audience || outputRecommendation?.title || priorityOpportunity?.title || "The audience most likely to rely on this decision.";
  const fallbackEvidencePattern = outputRecommendation?.evidencePattern || outputRecommendation?.sourceInsight || priorityOpportunity?.detail || "A reviewed visibility or recommendation gap is driving this prescription.";
  const fallbackExpectedResult = outputRecommendation?.expectedResult || outputRecommendation?.detail || "A clearer reviewed asset ready for the next human review.";
  const sourceRecommendationId = safeText(
    source.sourceRecommendationId || source.source_recommendation_id || source.recommendationId || source.recommendation_id || outputRecommendation?.sourceRecommendationId,
    "",
  );
  const actionBriefStatus = safeText(source.actionBriefStatus || source.action_brief_status, "");
  return {
    outputType: safeText(source.outputType || source.output_type || source.type, outputRecommendation?.outputType || "Recommended output pending reviewed signal"),
    targetAsset: safeText(source.targetAsset || source.target_asset || source.asset, fallbackTitle || "Target asset unavailable"),
    objective: safeText(source.objective || source.goal || source.outcome, outputRecommendation?.detail || "Recommended output pending reviewed signal."),
    evidenceReason: safeText(source.evidenceReason || source.evidence_reason || source.reason, fallbackReason),
    primaryNextAction: safeText(source.primaryNextAction || source.primary_next_action || source.nextAction || source.next_action, fallbackAction),
    audience: safeText(source.audience || source.targetAudience || source.target_audience || source.forAudience || source.for_audience, fallbackAudience),
    evidencePattern: safeText(source.evidencePattern || source.evidence_pattern || source.evidenceSignal || source.evidence_signal || source.sourceInsight || source.source_insight, fallbackEvidencePattern),
    expectedResult: safeText(source.expectedResult || source.expected_result || source.deliverableOutcome || source.deliverable_outcome || source.desiredOutcome || source.desired_outcome, fallbackExpectedResult),
    platformFit: safeText(source.platformFit || source.platform_fit, "Platform fit not specified."),
    sourceRecommendationTitle: safeText(source.sourceRecommendationTitle || source.source_recommendation_title, fallbackTitle || "No source recommendation available"),
    ...(sourceRecommendationId ? { sourceRecommendationId } : {}),
    ...(actionBriefStatus ? { actionBriefStatus } : {}),
  };
}

function safeReviewedOutputPreviews(value: unknown): CustomerCaiReviewedOutputPreview[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_REVIEWED_OUTPUT_PREVIEWS).map((item) => {
    const source = item && typeof item === "object" ? item as Record<string, unknown> : {};
    const rawSections = Array.isArray(source.sections) ? source.sections : [];
    const sections = rawSections.slice(0, MAX_REVIEWED_OUTPUT_PREVIEW_SECTIONS).map((section) => {
      if (typeof section === "string") {
        return { heading: "Reviewed section", body: safeText(section, "Reviewed section detail is unavailable.") };
      }
      const value = section && typeof section === "object" ? section as Record<string, unknown> : {};
      return {
        heading: safeText(value.heading || value.title || value.label, "Reviewed section"),
        body: safeText(value.body || value.detail || value.summary, "Reviewed section detail is unavailable."),
      };
    });
    const status = safeText(source.status, "");
    const priority = safeText(source.priority, "");
    return {
      title: safeText(source.title || source.label || source.name, "Reviewed output preview"),
      outputType: safeText(source.outputType || source.output_type || source.type, "Read-only brief"),
      linkedRecommendationTitle: safeText(
        source.linkedRecommendationTitle || source.linked_recommendation_title || source.recommendationTitle || source.recommendation_title,
        "Linked CAI recommendation",
      ),
      summary: safeText(source.summary || source.detail || source.description, "Reviewed preview content is available."),
      sections,
      ...(status ? { status } : {}),
      ...(priority ? { priority } : {}),
    };
  });
}

function safeReviewedOutputArtifacts(value: unknown): CustomerCaiReviewedOutputArtifact[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_REVIEWED_OUTPUT_ARTIFACTS).map((item) => {
    const source = item && typeof item === "object" ? item as Record<string, unknown> : {};
    const rawSections = Array.isArray(source.sections) ? source.sections : [];
    const sections = rawSections.slice(0, MAX_REVIEWED_OUTPUT_ARTIFACT_SECTIONS).map((section) => {
      const value = section && typeof section === "object" ? section as Record<string, unknown> : {};
      const rawBullets = Array.isArray(value.bullets) ? value.bullets : typeof value.bullets === "string" ? [value.bullets] : [];
      return {
        heading: safeText(value.heading || value.title || value.label, "Reviewed section"),
        body: safeText(value.body || value.detail || value.summary, "Reviewed section detail is unavailable."),
        bullets: Array.from(new Set(rawBullets.map((bullet) => safeText(bullet)).filter(Boolean))).slice(0, MAX_REVIEWED_OUTPUT_ARTIFACT_BULLETS),
      };
    });
    const status = safeText(source.status, "");
    const priority = safeText(source.priority, "");
    const reviewerNote = safeText(source.reviewerNote || source.reviewer_note, "");
    return {
      title: safeText(source.title || source.label || source.name, "Reviewed output artifact"),
      outputType: safeText(source.outputType || source.output_type || source.type, "Reviewed brief"),
      linkedRecommendationTitle: safeText(source.linkedRecommendationTitle || source.linked_recommendation_title || source.recommendationTitle || source.recommendation_title, "Linked CAI recommendation"),
      summary: safeText(source.summary || source.detail || source.description, "Reviewed artifact content is available."),
      sections,
      ...(status ? { status } : {}),
      ...(priority ? { priority } : {}),
      ...(reviewerNote ? { reviewerNote } : {}),
    };
  });
}

function safeActions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.map((item) => safeText(item)).filter(Boolean))).slice(0, MAX_ITEMS);
}

export function isCustomerCaiEligible(account: AccountLike | null | undefined): boolean {
  return isCaiGenerationEnabled(account);
}

export function buildCustomerCaiReadOnlyModel(
  account: AccountLike | null | undefined,
  suppliedModel?: unknown,
): CustomerCaiReadOnlyModel {
  const config = account?.local_config;
  const source = suppliedModel && typeof suppliedModel === "object"
    ? suppliedModel as Record<string, unknown>
    : config?.current_asset_intelligence_customer_read_model && typeof config.current_asset_intelligence_customer_read_model === "object"
      ? config.current_asset_intelligence_customer_read_model as Record<string, unknown>
      : {};
  const assetsReviewed = safeCount(source.assetsReviewed ?? source.assets_reviewed ?? source.assetCount);
  const reviewedInputCount = safeCount(
    source.reviewedInputCount
      ?? source.reviewed_input_count
      ?? source.inputCount
      ?? source.signalsReviewed
      ?? source.signals_reviewed,
  ) ?? assetsReviewed;
  const reviewedInputLabel = safeText(
    source.reviewedInputLabel
      || source.reviewed_input_label
      || source.inputLabel
      || source.signalsReviewedLabel
      || source.signals_reviewed_label,
    "Signals reviewed",
  );
  const priorityOpportunities = safeItems(source.priorityOpportunities || source.priority_opportunities || source.opportunities);
  const assetGaps = safeItems(source.assetGaps || source.asset_gaps || source.gaps);
  const recommendedNextActions = safeActions(source.recommendedNextActions || source.recommended_next_actions || source.nextActions);
  const outputRecommendations = safeOutputRecommendations(source.outputRecommendations || source.output_recommendations);
  const primaryReviewedOpportunity = priorityOpportunities[0] || assetGaps[0];
  const outputPrescription = safeOutputPrescription(
    source.outputPrescription || source.output_prescription || source.prescribedOutput,
    outputRecommendations[0],
    primaryReviewedOpportunity,
    recommendedNextActions[0],
  );
  return {
    summary: safeText(source.summary || source.overview, "Current Asset Intelligence is available as a read-only account review."),
    readiness: safeText(source.readiness || source.status, "Review available"),
    assetsReviewed,
    reviewedInputLabel,
    reviewedInputCount,
    priorityOpportunities,
    assetGaps,
    recommendedNextActions,
    outputPrescription,
    outputRecommendations,
    reviewedOutputPreviews: safeReviewedOutputPreviews(source.reviewedOutputPreviews || source.reviewed_output_previews),
    reviewedOutputArtifacts: safeReviewedOutputArtifacts(source.reviewedOutputArtifacts || source.reviewed_output_artifacts),
  };
}

export { MAX_REVIEWED_OUTPUT_PREVIEWS, MAX_REVIEWED_OUTPUT_PREVIEW_SECTIONS, MAX_REVIEWED_OUTPUT_ARTIFACTS, MAX_REVIEWED_OUTPUT_ARTIFACT_SECTIONS, MAX_REVIEWED_OUTPUT_ARTIFACT_BULLETS };
